import { ampli } from '@/ampli';
import { useMedia } from 'react-use';

import { useLiquidityScore } from '@/hooks/useLiquidityScore';

import { fontStyles } from '@/styles/typography';

import { InfoIconOutline } from '@/ui/Icons/InfoIconOutline';

import { type ZoraType } from '@/utils/zora';

import { ChainAddress } from '../../../../utils/models';
import { LiquidityScoreBadge } from '../../../LiquidityScoreBadge';
import { tokenProfileHeaderBreakpoints } from '../../TokenProfileHeader/responsive';
import { DropletsIcon } from './Droplets';
import { ScoreTooltip } from './ScoreTooltip';

interface LiquidityScoreProps {
  /** Token to display liquidity score for */
  activeToken: {
    address: string;
    chainId: number;
    chainName: string;
    numBlockedTokenLists: number;
    zoraType: ZoraType;
    symbol: string;
  };
}

export const LiquidityScore = ({ activeToken }: LiquidityScoreProps) => {
  const { data } = useLiquidityScore([
    { address: activeToken.address, chainId: activeToken.chainId },
  ]);
  const isMobileLinksView = useMedia(tokenProfileHeaderBreakpoints.mobileLinks, false);
  const score = getVisibleLiquidityScore(data?.data?.[0]?.score, activeToken.numBlockedTokenLists);

  const handleScoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const liquidityScore = document.getElementById('liquidityScore');

    if (liquidityScore) {
      liquidityScore.scrollIntoView({ behavior: 'smooth' });
    }
    ampli.liquidityButtonClicked({
      token_chain_id: activeToken.chainId,
      token_chain_name: activeToken.chainName,
      token_address: activeToken.address,
      token_symbol: activeToken.symbol,
    });
  };

  return (
    <div className="mx-1">
      {!isMobileLinksView && (
        <ScoreTooltip
          isMobile={isMobileLinksView}
          currentScore={score}
          zoraType={activeToken.zoraType}
        >
          <div
            className={fontStyles({
              variant: 'LABEL_XS',
              class: [
                'text-token-gray-800',
                'flex',
                'min-w-fit',
                'font-normal',
                'ml-1',
                'items-center',
                'mb-[7px]',
              ],
            })}
          >
            Liquidity{' '}
            <InfoIconOutline className="cursor-default h-3 ml-1 w-3 text-token-gray-700 hover:cursor-default" />
          </div>
        </ScoreTooltip>
      )}
      <LiquidityScoreBadge
        address={activeToken.address as ChainAddress}
        chainId={activeToken.chainId}
        onClick={handleScoreClick}
        className="cursor-pointer"
        score={score}
        scoreLoading={false}
      />
    </div>
  );
};

export const getScoreIconAndStyles = (score?: number) => {
  const baseClass =
    'flex items-center justify-center w-[68px] h-8 px-6 cursor-pointer rounded-lg text-sm font-medium transition-all duration-200 ease-in-out';

  if (score === undefined || score === null) {
    return {
      containerClass: fontStyles({
        variant: 'LABEL_SM',
        class: [`${baseClass} bg-token-gray-300 text-token-gray-800 hover:bg-token-gray-400`],
      }),
      icon: <DropletsIcon.Unknown />,
    };
  }

  if (score >= 6)
    return {
      containerClass: fontStyles({
        variant: 'LABEL_SM',
        class: [`${baseClass} bg-token-green-300 text-token-green-900 hover:bg-token-green-400`],
      }),
      icon: <DropletsIcon.High />,
    };
  if (score >= 3)
    return {
      containerClass: fontStyles({
        variant: 'LABEL_SM',
        class: [`${baseClass} bg-token-orange-300 text-token-orange-900 hover:bg-token-orange-400`],
      }),
      icon: <DropletsIcon.Medium />,
    };
  return {
    containerClass: fontStyles({
      variant: 'LABEL_SM',
      class: [`${baseClass} bg-token-red-300 text-token-red-900 hover:bg-token-red-400`],
    }),
    icon: <DropletsIcon.Low />,
  };
};

export const getVisibleLiquidityScore = (
  score: number | undefined,
  blockedTokenListsCount?: number | null,
) => {
  if ((blockedTokenListsCount ?? 0) > 0) return undefined;
  return score;
};
